import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Minus } from "lucide-react";
import { MONORAIL } from "../lib/monorail";

/**
 * The engraved layer over the World — orbs, names, wordmark, taglines, seal.
 *
 * Founder's rule from day one: no baked-in text in the artwork. Every word and
 * every mark on the map is HTML, positioned as a percentage of the PLATE, so a
 * re-generated map means re-measuring these numbers and nothing else.
 *
 * Coordinates are taken verbatim from the founder's Design build: an orb sits on
 * the entry of each Place, and the name hangs off it by a measured offset.
 * `dx` is a percentage of plate width, `dy` of plate height.
 *
 * This component must be rendered INSIDE the plate box (contain-fitted, aspect
 * 16/9 for Ghost World, `container-name: worldmap`) — see Atlas.jsx.
 *
 * -----------------------------------------------------------------------
 * TUNE MODE — visit any page with ?tune=1 in the URL.
 * -----------------------------------------------------------------------
 * Every orb and every engraved name becomes drag-to-position. A floating
 * panel on the right shows the live AT config with a one-click copy of the
 * JSON. Positions persist to localStorage so a refresh doesn't lose them.
 * Reset button snaps everything back to shipped defaults.
 *
 * This is the founder's granular-nudge cost eliminator: burn credits on
 * building the tool ONCE, then iterate in-browser without asking the agent
 * to move things `%` at a time. Paste the exported JSON back to me and I
 * bake it into the AT constant below.
 */
const SHORT = {
  arcade: "Arcade",
  arena: "Arena",
  ops: "Ops",
  chamber: "Chambers",
  bridge: "Bridge",
  hall: "Hall",
  vault: "Vault",
  commons: "Commons",
};

const AT = {
  arcade: { orb: [33.1, 27.3], dx: -2.9, dy: 5.1, color: "#ffe6a8", rest: 1 },
  arena: { orb: [51.6, 31.4], dx: 4.4, dy: 3.1, color: "#fbe0bc", rest: 0.86 },
  ops: { orb: [69.2, 32.5], dx: 5.7, dy: 4.8, color: "#f7e6b4", rest: 0.86 },
  chamber: { orb: [56.3, 46.4], dx: 8.7, dy: 8.6, color: "#fbecc4", rest: 0.86 },
  bridge: { orb: [55.9, 60.7], dx: 3.5, dy: 14.6, color: "#bfe6f8", rest: 0.88, cool: true },
  hall: { orb: [43.9, 75.5], dx: 0.5, dy: 6.1, color: "#ffe6a8", rest: 1 },
  vault: { orb: [33.1, 66], dx: -3.7, dy: 16.2, color: "#c6e6f8", rest: 0.9, cool: true },
  commons: { orb: [25.4, 55.2], dx: 0, dy: 5.6, color: "#fbe8ae", rest: 0.86 },
};

/** Non-orb objects that also get drag handles in tune mode. Every entry is
 *  a single [x, y] in plate-percent coordinates. Adding a new tunable static
 *  element = one row here + one branch in the render. */
const ATLAS_STATIC = {
  emblem: [48.8, 46.8],
  horizon_map: [73.9, 66.6],
  pursuits_call: [78.6, 48.2],
};

const WARM_SHADOW =
  "0 2px 6px rgba(0,0,0,1), 0 0 14px rgba(0,0,0,1), 0 0 32px rgba(0,0,0,0.98), 0 0 62px rgba(0,0,0,0.92), 0 0 110px rgba(0,0,0,0.55)";
const COOL_SHADOW =
  "0 2px 6px rgba(2,10,20,1), 0 0 14px rgba(2,10,20,1), 0 0 32px rgba(2,10,20,0.98), 0 0 62px rgba(2,10,20,0.92), 0 0 110px rgba(2,10,20,0.55)";

/** The pulsating orb that marks an entry. Open Places burn; sealed ones glimmer. */
const ORB = "clamp(22px, 1.7cqw, 34px)";

const Orb = ({ open, cool, delay }) => (
  <span className="absolute inset-0 grid place-items-center" aria-hidden>
    <motion.span
      className="absolute inset-0 rounded-full border border-current"
      style={{ boxShadow: "0 0 5px rgba(8,5,2,0.55)" }}
      animate={{ scale: [0.72, 1.65, 1.65], opacity: [0.85, 0, 0] }}
      transition={{ duration: 3.4, delay, repeat: Infinity, ease: "easeOut" }}
    />
    <motion.span
      className="rounded-full"
      style={{
        width: open ? "clamp(12px, 0.86cqw, 18px)" : "clamp(11px, 0.78cqw, 16px)",
        height: open ? "clamp(12px, 0.86cqw, 18px)" : "clamp(11px, 0.78cqw, 16px)",
        background:
          "radial-gradient(circle at 50% 45%, #ffffff 0%, #fff6de 34%, currentColor 72%)",
        boxShadow: open
          ? "0 0 0 2px rgba(8,5,2,0.5), 0 0 18px 5px rgba(255,196,80,0.95), 0 0 40px 10px rgba(255,150,40,0.45)"
          : cool
            ? "0 0 0 2px rgba(4,12,20,0.55), 0 0 15px 4px rgba(150,205,240,0.95), 0 0 36px 10px rgba(110,175,220,0.5)"
            : "0 0 0 2px rgba(8,5,2,0.55), 0 0 16px 5px rgba(255,214,140,0.95), 0 0 38px 11px rgba(255,170,70,0.5)",
      }}
      animate={{ opacity: [0.82, 1, 0.82], scale: [0.94, 1.08, 0.94] }}
      transition={{ duration: 3.4, delay, repeat: Infinity, ease: "easeInOut" }}
    />
  </span>
);

/**
 * The two open doors, as solid gold. Founder's placement: PLAY in the wedge of
 * bridges between the Arcade and the Arena, STAGE beside the Hall below the
 * Bridge.
 */
const DOORS = [
  { key: "play", label: "Play", at: [40.1, 30.2], to: "/playforward", sub: "Arcade", portal: "playforward" },
  { key: "stages", label: "Stages", at: [53.5, 81.9], to: "/stageward/walk", sub: "Hall", portal: "stageward" },
];

const Door = ({ door, beat, i, onGo, tune, startDrag, pos }) => {
  const at = pos || door.at;
  return (
    <motion.button
      type="button"
      data-testid={`atlas-door-${door.key}`}
      onClick={() => !tune && onGo(door.portal, door.to)}
      onPointerDown={tune ? startDrag(`door_${door.key}`, "pos") : undefined}
      className={`door-${door.key} absolute z-20 flex flex-col items-center rounded-full px-[1.15cqw] py-[0.72cqh] ${tune ? "cursor-move" : ""}`}
      style={{
        left: `${at[0]}%`,
        top: `${at[1]}%`,
        x: "-50%",
        y: "-50%",
        background: "linear-gradient(178deg, #ffe6a8 0%, #e8a030 52%, #c47d18 100%)",
        boxShadow:
          "0 0 0 1px rgba(72,38,4,0.5), 0 8px 22px -6px rgba(0,0,0,0.85), 0 0 34px -6px rgba(255,190,80,0.6)",
        touchAction: tune ? "none" : undefined,
      }}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={!tune ? { scale: 1.06 } : undefined}
      transition={{ duration: 0.9, delay: beat + 0.9 + i * 0.18, ease: [0.16, 0.84, 0.24, 1] }}
    >
      <span
        className="engrave font-extrabold leading-none text-[#1a0c04]"
        style={{ fontSize: "clamp(9px, 1.07cqw, 17px)", letterSpacing: "0.13em" }}
      >
        {door.label.toUpperCase()}
      </span>
      <span
        className="mono uppercase leading-none text-[#1a0c04]"
        style={{ fontSize: "clamp(5.5px, 0.52cqw, 9px)", letterSpacing: "0.18em", marginTop: "0.16cqh", opacity: 0.95 }}
      >
        {door.sub}
      </span>
    </motion.button>
  );
};

// ---------------------------------------------------------- TUNE MODE

const TUNE_STORAGE_KEY = "endeavorum:atlas-labels:tune-v2";

/** Read `?tune=1` from window.location without pulling react-router in here. */
const isTuneMode = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("tune") === "1";
};

/** Convert AT + DOORS + emblem to draggable state (absolute % of plate for
 *  every handle). Doors carry a single 'pos'; emblem has a single 'pos'. */
const atToPositions = () => {
  const out = {};
  Object.entries(AT).forEach(([k, v]) => {
    out[k] = {
      orb: [v.orb[0], v.orb[1]],
      label: [v.orb[0] + v.dx, v.orb[1] + v.dy],
    };
  });
  DOORS.forEach((d) => {
    out[`door_${d.key}`] = { pos: [d.at[0], d.at[1]] };
  });
  Object.entries(ATLAS_STATIC).forEach(([k, xy]) => {
    out[k] = { pos: [xy[0], xy[1]] };
  });
  return out;
};

/** Convert positions back to AT shape (orb + dx/dy) so the founder can paste
 *  it into the AT constant. Rounds to 1 decimal for readability. */
const positionsToAT = (positions) => {
  const round = (n) => Math.round(n * 10) / 10;
  const out = {};
  Object.entries(AT).forEach(([k, base]) => {
    const v = positions[k];
    out[k] = {
      orb: [round(v.orb[0]), round(v.orb[1])],
      dx: round(v.label[0] - v.orb[0]),
      dy: round(v.label[1] - v.orb[1]),
      color: base.color,
      rest: base.rest,
    };
    if (base.cool) out[k].cool = true;
  });
  return out;
};

/** Pretty-print all three tunable blocks (AT + DOORS + emblem) so a paste-back
 *  is a literal find-and-replace against three named consts. */
const formatATForCopy = (positions) => {
  const round = (n) => Math.round(n * 10) / 10;
  const at = positionsToAT(positions);
  const rows = Object.entries(at)
    .map(([k, v]) => {
      const cool = v.cool ? ", cool: true" : "";
      return `  ${k}: { orb: [${v.orb[0]}, ${v.orb[1]}], dx: ${v.dx}, dy: ${v.dy}, color: "${v.color}", rest: ${v.rest}${cool} },`;
    })
    .join("\n");
  const doors = DOORS.map((d) => {
    const p = positions[`door_${d.key}`].pos;
    return `  { key: "${d.key}", label: "${d.label}", at: [${round(p[0])}, ${round(p[1])}], to: "${d.to}", sub: "${d.sub}", portal: "${d.portal}" },`;
  }).join("\n");
  const statics = Object.keys(ATLAS_STATIC)
    .map((k) => {
      const p = positions[k].pos;
      return `  ${k}: [${round(p[0])}, ${round(p[1])}],`;
    })
    .join("\n");
  return (
    `const AT = {\n${rows}\n};\n\n` +
    `const DOORS = [\n${doors}\n];\n\n` +
    `const ATLAS_STATIC = {\n${statics}\n};`
  );
};

/** Custom hook: pointer drag on any element, reports position as % of the
 *  passed-in rectRef container. Bounded 0-100. */
const usePlateDrag = (plateRef, onDrag) => {
  const draggingRef = useRef(null);
  const startHandler = useCallback(
    (key, kind) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = { key, kind, pointerId: e.pointerId };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    []
  );
  useEffect(() => {
    const onMove = (e) => {
      const drag = draggingRef.current;
      if (!drag) return;
      const rect = plateRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
      onDrag(drag.key, drag.kind, [x, y]);
    };
    const onUp = () => {
      draggingRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [plateRef, onDrag]);
  return startHandler;
};

const TuneHUD = ({ positions, onReset, onExit }) => {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // HUD lives in viewport coords, not plate coords — pixel offsets from the
  // top-left. Persist so a drag survives a refresh.
  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return { top: 96, left: null, right: 16 };
    try {
      const raw = localStorage.getItem("endeavorum:atlas-tune-hud");
      if (raw) return JSON.parse(raw);
    } catch (err) {
      // Corrupt or unavailable storage — fall through to defaults.
      console.debug("[AtlasLabels] tune-hud storage read failed:", err);
    }
    return { top: 96, left: null, right: 16 };
  });
  useEffect(() => {
    try {
      localStorage.setItem("endeavorum:atlas-tune-hud", JSON.stringify(pos));
    } catch (err) {
      console.debug("[AtlasLabels] tune-hud storage write failed:", err);
    }
  }, [pos]);

  const dragging = useRef(null);
  const onGrip = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startPos: pos,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [pos]);
  useEffect(() => {
    const onMove = (e) => {
      const drag = dragging.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const startLeft =
        drag.startPos.left != null
          ? drag.startPos.left
          : window.innerWidth - 380 - drag.startPos.right;
      const newLeft = Math.max(6, Math.min(window.innerWidth - 60, startLeft + dx));
      const newTop = Math.max(6, Math.min(window.innerHeight - 60, drag.startPos.top + dy));
      setPos({ top: newTop, left: newLeft, right: null });
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const text = formatATForCopy(positions);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  const style = {
    top: pos.top,
    left: pos.left != null ? pos.left : undefined,
    right: pos.left == null ? pos.right : undefined,
    boxShadow: "0 0 0 1px rgba(0,0,0,0.6), 0 30px 80px -20px rgba(0,0,0,0.9)",
  };

  if (collapsed) {
    // Collapsed = a small pill you can still grab-and-drag OR expand.
    return (
      <div
        className="fixed z-[100] flex items-center gap-2 rounded-full border border-[#f2c877]/40 bg-[#0b0704]/95 py-1.5 pl-1.5 pr-3 shadow-2xl backdrop-blur-md"
        style={style}
        data-testid="atlas-tune-hud-collapsed"
      >
        <button
          type="button"
          onPointerDown={onGrip}
          className="mono cursor-move rounded-full bg-[#f2c877]/20 px-2 py-1 text-[9px] uppercase tracking-[0.28em] text-[#f2c877]"
          style={{ touchAction: "none" }}
          aria-label="Drag tune HUD"
        >
          ⋮⋮
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="mono text-[10px] uppercase tracking-[0.28em] text-[#f2c877] transition hover:text-white"
          data-testid="atlas-tune-expand"
        >
          Expand
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed z-[100] w-[min(92vw,380px)] rounded-2xl border border-[#f2c877]/40 bg-[#0b0704]/95 shadow-2xl backdrop-blur-md"
      style={style}
      data-testid="atlas-tune-hud"
    >
      {/* Drag-grip header — grabbing this moves the whole panel */}
      <div
        className="flex cursor-move items-center justify-between gap-3 rounded-t-2xl border-b border-white/10 bg-[#f2c877]/8 px-4 py-2.5 select-none"
        onPointerDown={onGrip}
        style={{ touchAction: "none" }}
        data-testid="atlas-tune-grip"
      >
        <div className="flex items-center gap-2">
          <span className="mono text-[9px] uppercase tracking-[0.32em] text-[#f2c877]/70">⋮⋮</span>
          <p className="mono text-[10px] uppercase tracking-[0.32em] text-[#f2c877]">
            Tune Mode
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
            aria-label="Collapse"
            data-testid="atlas-tune-collapse"
          >
            <Minus size={14} />
          </button>
          <button
            type="button"
            onClick={onExit}
            className="rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white"
            aria-label="Exit tune mode"
            data-testid="atlas-tune-exit"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="p-4">
        <p className="serif text-[12px] leading-snug text-white/70">
          Drag any orb, engraved name, PLAY/STAGES button, or the centre seal.
          Copy the JSON when you like where things sit and paste it back to
          the builder.
        </p>
        <pre
          className="mono mt-3 max-h-[220px] overflow-auto rounded-lg border border-white/10 bg-black/60 p-2 text-[10.5px] leading-[1.35] text-[#e2cba2]"
          data-testid="atlas-tune-json"
        >
          {text}
        </pre>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={copy}
            className="mono flex-1 rounded-full border border-[#f2c877] bg-[#c9a227]/20 px-3 py-2 text-[11px] uppercase tracking-[0.24em] text-[#f2c877] transition hover:bg-[#c9a227]/40"
            data-testid="atlas-tune-copy"
          >
            {copied ? "copied ✓" : "copy json"}
          </button>
          <button
            type="button"
            onClick={onReset}
            className="mono rounded-full border border-white/20 bg-white/5 px-3 py-2 text-[11px] uppercase tracking-[0.24em] text-white/70 transition hover:bg-white/10 hover:text-white"
            data-testid="atlas-tune-reset"
          >
            reset
          </button>
        </div>
        <p className="mono mt-3 text-[9px] uppercase tracking-[0.32em] text-white/40">
          Drag me by the header · Auto-saved · Minimise for a clear view
        </p>
      </div>
    </div>
  );
};

// -------------------------------------------------------- MAIN COMPONENT

export const AtlasLabels = ({ beat = 0.5, onEnter }) => {
  const tune = isTuneMode();

  // Positions state only exists in tune mode; non-tune renders AT directly.
  // Any stored positions are MERGED with fresh defaults so an older schema in
  // localStorage (e.g. before doors and emblem were tunable) does not crash
  // the HUD by leaving `positions.door_play` undefined.
  const [positions, setPositions] = useState(() => {
    const defaults = atToPositions();
    if (!tune || typeof window === "undefined") return defaults;
    try {
      const raw = localStorage.getItem(TUNE_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        return { ...defaults, ...saved };
      }
    } catch {}
    return defaults;
  });
  useEffect(() => {
    if (!tune) return;
    try {
      localStorage.setItem(TUNE_STORAGE_KEY, JSON.stringify(positions));
    } catch {}
  }, [tune, positions]);

  const layerRef = useRef(null);

  const onDrag = useCallback((key, kind, xy) => {
    setPositions((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [kind]: xy,
      },
    }));
  }, []);
  const startDrag = usePlateDrag(layerRef, onDrag);

  const resetTune = () => {
    setPositions(atToPositions());
    try {
      localStorage.removeItem(TUNE_STORAGE_KEY);
    } catch {}
  };
  const closeTune = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("tune");
    window.location.href = url.toString();
  };

  return (
    <div ref={layerRef} className="absolute inset-0 z-10" data-testid="atlas-label-layer">
      {DOORS.map((d, i) => (
        <Door
          key={d.key}
          door={d}
          beat={beat}
          i={i}
          onGo={onEnter}
          tune={tune}
          startDrag={startDrag}
          pos={tune ? positions[`door_${d.key}`].pos : null}
        />
      ))}

      {MONORAIL.map((p, i) => {
        const staticAt = AT[p.key];
        if (!staticAt) return null;
        const delay = beat + 0.5 + i * 0.12;

        // In tune mode, orb sits at the free-form position; the label is
        // rendered as a SEPARATE draggable overlay (not offset inside the
        // orb) so it can move independently anywhere on the plate.
        const orbPos = tune ? positions[p.key].orb : staticAt.orb;
        const labelAbs = tune
          ? positions[p.key].label
          : [staticAt.orb[0] + staticAt.dx, staticAt.orb[1] + staticAt.dy];

        return (
          <div key={p.key}>
            <motion.button
              type="button"
              data-testid={`atlas-label-${p.key}`}
              disabled={!p.open && !tune}
              onClick={() => !tune && p.to && onEnter(p.portal, p.to)}
              onPointerDown={tune ? startDrag(p.key, "orb") : undefined}
              title={SHORT[p.key]}
              className={`absolute grid -translate-x-1/2 -translate-y-1/2 place-items-center transition-transform duration-500 enabled:hover:scale-[1.09] disabled:cursor-default ${tune ? "cursor-move" : ""}`}
              style={{
                left: `${orbPos[0]}%`,
                top: `${orbPos[1]}%`,
                width: ORB,
                height: ORB,
                color: staticAt.color,
                touchAction: tune ? "none" : undefined,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.1, delay }}
            >
              <Orb open={p.open} cool={staticAt.cool} delay={delay} />
              {/* Label is rendered inline (as before) only when NOT tuning —
                  in tune mode we render it externally so it can be dragged. */}
              {!tune && (
                <span
                  className="engrave wp-label absolute block whitespace-nowrap"
                  style={{
                    left: `calc(50% + ${staticAt.dx}cqw)`,
                    top: `calc(50% + ${staticAt.dy}cqh)`,
                    transform: "translateX(-50%)",
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: staticAt.cool ? "#eaf6ff" : "currentColor",
                    opacity: staticAt.rest,
                    fontSize: staticAt.cool
                      ? "clamp(13px, 1.45cqw, 24px)"
                      : "clamp(12px, 1.3cqw, 21px)",
                    textShadow: staticAt.cool ? COOL_SHADOW : WARM_SHADOW,
                  }}
                >
                  {SHORT[p.key]}
                </span>
              )}
            </motion.button>

            {tune && (
              <div
                className="absolute z-20 -translate-x-1/2 -translate-y-1/2 cursor-move select-none whitespace-nowrap rounded-md border border-[#f2c877]/60 bg-[#0b0704]/70 px-2 py-1 backdrop-blur-sm"
                style={{
                  left: `${labelAbs[0]}%`,
                  top: `${labelAbs[1]}%`,
                  touchAction: "none",
                }}
                onPointerDown={startDrag(p.key, "label")}
                data-testid={`atlas-tune-label-${p.key}`}
              >
                <span
                  className="engrave block"
                  style={{
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: staticAt.cool ? "#eaf6ff" : staticAt.color,
                    fontSize: staticAt.cool
                      ? "clamp(13px, 1.45cqw, 24px)"
                      : "clamp(12px, 1.3cqw, 21px)",
                    textShadow: staticAt.cool ? COOL_SHADOW : WARM_SHADOW,
                  }}
                >
                  {SHORT[p.key]}
                </span>
              </div>
            )}
          </div>
        );
      })}

      {/* The Horizon Map is an Artifact, not a door. Unclickable, on purpose.
          In tune mode it becomes drag-to-position like everything else. */}
      <motion.div
        className={`sky-type ${tune ? "pointer-events-auto cursor-move" : "pointer-events-none"} absolute text-center`}
        style={{
          left: `${tune ? positions.horizon_map.pos[0] : ATLAS_STATIC.horizon_map[0]}%`,
          top: `${tune ? positions.horizon_map.pos[1] : ATLAS_STATIC.horizon_map[1]}%`,
          zIndex: 7,
          x: "-50%",
          touchAction: tune ? "none" : undefined,
        }}
        onPointerDown={tune ? startDrag("horizon_map", "pos") : undefined}
        data-testid="atlas-horizon-map"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: beat + 1 }}
      >
        <p
          className="engrave whitespace-nowrap"
          style={{
            fontWeight: 700,
            fontSize: "clamp(13px, 1.45cqw, 24px)",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "#eaf6ff",
            textShadow: COOL_SHADOW,
          }}
        >
          Horizon Map
        </p>
      </motion.div>

      {/* Founder's call-to-endeavor stack — to the right of the Chambers.
          Three short lines that name what a visitor DOES here. Left-aligned
          so it can float naturally within the Horizon Map boundary. */}
      <motion.div
        className={`${tune ? "pointer-events-auto cursor-move" : "pointer-events-none"} absolute text-left`}
        style={{
          left: `${tune ? positions.pursuits_call.pos[0] : ATLAS_STATIC.pursuits_call[0]}%`,
          top: `${tune ? positions.pursuits_call.pos[1] : ATLAS_STATIC.pursuits_call[1]}%`,
          zIndex: 8,
          x: "-50%",
          y: "-50%",
          touchAction: tune ? "none" : undefined,
        }}
        onPointerDown={tune ? startDrag("pursuits_call", "pos") : undefined}
        data-testid="atlas-pursuits-call"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.4, delay: beat + 1.15 }}
      >
        {["Roll up your sleeves,", "Craft your Pursuits,", "Game-Plans to Execute!"].map(
          (line) => (
            <p
              key={line}
              className="serif whitespace-nowrap font-semibold"
              style={{
                fontSize: "clamp(11.5px, 1.05cqw, 18px)",
                letterSpacing: "0.02em",
                color: "#fdf3d8",
                textShadow: WARM_SHADOW,
                lineHeight: 1.35,
              }}
            >
              {line}
            </p>
          )
        )}
      </motion.div>

      <motion.img
        src="/brand/endeavorum-emblem.png"
        alt="Endeavorum seal"
        data-testid="atlas-seal"
        onPointerDown={tune ? startDrag("emblem", "pos") : undefined}
        className={`pointer-events-${tune ? "auto" : "none"} absolute -translate-x-1/2 -translate-y-1/2 ${tune ? "cursor-move" : ""}`}
        style={{
          left: `${tune ? positions.emblem.pos[0] : ATLAS_STATIC.emblem[0]}%`,
          top: `${tune ? positions.emblem.pos[1] : ATLAS_STATIC.emblem[1]}%`,
          width: "4.96cqw",
          minWidth: 44,
          touchAction: tune ? "none" : undefined,
        }}
        animate={{
          opacity: [0.62, 0.9, 0.62],
          filter: [
            "drop-shadow(0 0 18px rgba(232,160,48,0.35))",
            "drop-shadow(0 0 34px rgba(232,160,48,0.6))",
            "drop-shadow(0 0 18px rgba(232,160,48,0.35))",
          ],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      {tune && (
        <TuneHUD positions={positions} onReset={resetTune} onExit={closeTune} />
      )}
    </div>
  );
};

export default AtlasLabels;
